import React from 'react';
import {FormattedMessage, injectIntl, intlShape, defineMessages} from 'react-intl';
import {connect} from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import styles from './loader.css';
import logo from './logo.svg';
import gmlogo from './gaiamod-logo.svg';
import {getIsLoadingWithId} from '../../reducers/project-state';
import {PackageOpen, FileJson, ShieldCheck, Blocks, Cat, Palette} from 'lucide-react';
import topBlock from './top-block.svg';
import middleBlock from './middle-block.svg';
import bottomBlock from './bottom-block.svg';
import TWRenderRecoloredImage from '../../lib/tw-recolor/render.jsx';
import {randomMessages} from './random-messages.jsx';

// Returns true when PotentiaMod is running inside an iframe.
const isInIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        // If window.top cannot be accessed, assume we are inside an iframe.
        return true;
    }
};

const mainMessages = {
    'gui.loader.headline': (
        <FormattedMessage
            defaultMessage="Loading Project"
            description="Main loading message"
            id="gui.loader.headline"
        />
    ),
    'gui.loader.creating': (
        <FormattedMessage
            defaultMessage="Creating Project"
            description="Main creating message"
            id="gui.loader.creating"
        />
    )
};

const messages = defineMessages({
    projectData: {
        defaultMessage: 'Loading project …',
        description: 'Appears when loading project data, but not assets yet',
        id: 'tw.loader.projectData'
    },
    downloadingAssets: {
        defaultMessage: 'Downloading assets ({complete}/{total}) …',
        description: 'Appears when loading project assets from a project on a remote website',
        id: 'tw.loader.downloadingAssets'
    },
    loadingAssets: {
        defaultMessage: 'Loading assets ({complete}/{total}) …',
        description: 'Appears when loading project assets from a project file on the user\'s computer',
        id: 'tw.loader.loadingAssets'
    }
});

const STAGE_ICONS = {
    projectData: PackageOpen,
    downloadingAssets: Cat,
    loadingAssets: Palette
};

const STAGE_PROGRESS = {
    projectData: [0, 50],
    downloadingAssets: [50, 55],
    loadingAssets: [55, 100]
};

class LoaderComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleAssetProgress',
            'handleProjectLoaded',
            'barInnerRef',
            'messageRef',
            'randomMessageRef',
            'updateRandomMessage'
        ]);
        this.barInnerEl = null;
        this.messageEl = null;
        this.randomMessageEl = null;
        this.ignoreProgress = false;
        this.randomMessageInterval = null;
        this.lastRandomMessageIndex = -1;
    }
    componentDidMount () {
        this.handleAssetProgress(
            this.props.vm.runtime.finishedAssetRequests,
            this.props.vm.runtime.totalAssetRequests
        );
        this.props.vm.on('ASSET_PROGRESS', this.handleAssetProgress);
        this.props.vm.runtime.on('PROJECT_LOADED', this.handleProjectLoaded);
        this.updateRandomMessage();
        this.randomMessageInterval = setInterval(this.updateRandomMessage, 3000);
    }
    componentWillUnmount () {
        this.props.vm.off('ASSET_PROGRESS', this.handleAssetProgress);
        this.props.vm.runtime.off('PROJECT_LOADED', this.handleProjectLoaded);
        clearInterval(this.randomMessageInterval);
    }
    updateRandomMessage () {
        if (this.randomMessageEl) {
            this.randomMessageEl.classList.remove(styles.randomMessageSlideIn);
            void this.randomMessageEl.offsetWidth; // Trigger reflow
            let randomIndex;
            do {
                randomIndex = Math.floor(Math.random() * randomMessages.length);
            } while (randomIndex === this.lastRandomMessageIndex);
            this.lastRandomMessageIndex = randomIndex;
            const randomFact = randomMessages[randomIndex];
            this.randomMessageEl.textContent = randomFact;
            this.randomMessageEl.classList.add(styles.randomMessageSlideIn);
            this.randomMessageEl.classList.add(styles.randomMessageRoulette);
        }
    }
    handleAssetProgress (finished, total) {
        if (this.ignoreProgress || !this.barInnerEl || !this.messageEl) {
            return;
        }

        if (total === 0) {
            // Started loading a new project.
            this.barInnerEl.style.width = '0';
            this.messageEl.textContent = this.props.intl.formatMessage(messages.projectData);
        } else {
            this.barInnerEl.style.width = `${finished / total * 100}%`;
            const message = this.props.isRemote ? messages.downloadingAssets : messages.loadingAssets;
            this.messageEl.textContent = this.props.intl.formatMessage(message, {
                complete: finished,
                total
            });
        }
    }
    handleProjectLoaded () {
        if (this.ignoreProgress || !this.barInnerEl || !this.messageEl) {
            return;
        }

        this.ignoreProgress = true;
        this.props.vm.runtime.resetProgress();
    }
    barInnerRef (barInner) {
        this.barInnerEl = barInner;
    }
    messageRef (message) {
        this.messageEl = message;
    }
    randomMessageRef (randomMessage) {
        this.randomMessageEl = randomMessage;
    }
    render () {
        return (
            <div
            className={classNames(styles.background, {
                [styles.fullscreen]: this.props.isFullScreen
            })}
            >
            
            <div className={styles.container}>
                <div className={styles.blockAnimation}>
                        <img src={logo} />
                    </div>
                <div className={styles.title}>
                {mainMessages[this.props.messageId]}
                </div>
               <div
                className={styles.tips}
                ref={this.randomMessageRef}
                />
                <div
                className={styles.message}
                ref={this.messageRef}
                />

                <div className={styles.barOuter}>
                <div
                    className={styles.barInner}
                    ref={this.barInnerRef}
                />
                </div>
				
				<a
                            className={styles.githubCta}
                            href="https://gaiamod-main.github.io/"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <img
                            width="100px"
                            src={gmlogo}
                            draggable={false}
                        />
                            <FormattedMessage
                                defaultMessage="🢀 Also, check out GaiaMod!"
                                description="Link on the loading screen to the GaiaMod page"
                                id="pot.loader.gaiamod"
                            />
							</a>
            </div>
            </div>
        );
    }
}

LoaderComponent.propTypes = {
    intl: intlShape,
    isFullScreen: PropTypes.bool,
    isRemote: PropTypes.bool,
    messageId: PropTypes.string,
    vm: PropTypes.shape({
        on: PropTypes.func,
        off: PropTypes.func,
        runtime: PropTypes.shape({
            totalAssetRequests: PropTypes.number,
            finishedAssetRequests: PropTypes.number,
            resetProgress: PropTypes.func,
            on: PropTypes.func,
            off: PropTypes.func
        })
    })
};
LoaderComponent.defaultProps = {
    isFullScreen: false,
    messageId: 'gui.loader.headline'
};

const mapStateToProps = state => ({
    isRemote: getIsLoadingWithId(state.scratchGui.projectState.loadingState),
    vm: state.scratchGui.vm
});

const mapDispatchToProps = () => ({});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(LoaderComponent));